import { CheckIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';

import { Button } from '@/components/chariot-ui';
import { signInWithRedirect } from 'aws-amplify/auth';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Input, Type } from '@/components/form/Input';
import { Loader } from '@/components/Loader';
import { Modal } from '@/components/Modal';
import { useAxios } from '@/hooks/useAxios';
import {
  useGenerateRecoveryCodes,
  useGetRecoveryCodesInfo,
} from '@/hooks/useRecoveryCodes';
import { MFAChallengeError, useStepUpAuth } from '@/hooks/useStepUpAuth';
import { useAuth } from '@/state/auth';

interface ViewRecoveryCodesModalProps {
  open: boolean;
  onClose: () => void;
}

export const ViewRecoveryCodesModal: React.FC<ViewRecoveryCodesModalProps> = ({
  open,
  onClose,
}) => {
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [hasExistingCodes, setHasExistingCodes] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showReauth, setShowReauth] = useState(false);
  const [password, setPassword] = useState('');
  const [reauthError, setReauthError] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState('');

  const { ssoDomain, me: userEmail } = useAuth();
  const { getInfo, isLoading: isLoadingInfo } = useGetRecoveryCodesInfo();
  const { mutateAsync: generateCodes, status: generateStatus } =
    useGenerateRecoveryCodes();
  const { getStepUpToken, confirmMFAAndGetToken } = useStepUpAuth();
  const axios = useAxios({ doNotImpersonate: true });

  const isSSO = Boolean(ssoDomain) && ssoDomain !== 'Google';

  useEffect(() => {
    const checkExistingCodes = async () => {
      try {
        const info = await getInfo();
        if (info && info?.total_codes && info.total_codes > 0) {
          setHasExistingCodes(true);
        } else {
          setHasExistingCodes(false);
        }
      } catch {
        setHasExistingCodes(false);
      }
    };

    const handleGenerateCodes = async () => {
      try {
        // After SSO redirect, the auth_time is fresh so we can get step-up token
        const response = await axios.post('/auth/step-up', {
          scope: 'recovery-codes',
        });
        const stepUpResult = response.data;
        const result = await generateCodes({
          count: 8,
          stepUpToken: stepUpResult.token,
        });
        if (result.codes) {
          setRecoveryCodes(result.codes);
          setHasExistingCodes(true);
        }
      } catch (error) {
        console.error('Failed to generate recovery codes after SSO:', error);
      }
    };

    if (open) {
      checkExistingCodes();
      // Check if returning from SSO re-authentication
      if (sessionStorage.getItem('pending_recovery_code_generation')) {
        sessionStorage.removeItem('pending_recovery_code_generation');
        handleGenerateCodes();
      }
    }
  }, [open, generateCodes, axios]);
  const handleReauthGenerate = async () => {
    setReauthError('');
    try {
      // Step 1: Re-auth with Cognito + get step-up token
      const stepUp = await getStepUpToken(
        userEmail,
        password,
        'recovery-codes'
      );

      // Step 2: Use step-up token to generate codes
      const response = await generateCodes({
        count: 8,
        stepUpToken: stepUp.token,
      });
      if (response.codes) {
        setRecoveryCodes(response.codes);
        setHasExistingCodes(true);
        setShowReauth(false);
        setPassword('');
      }
    } catch (error) {
      if (error instanceof MFAChallengeError) {
        setMfaRequired(true);
        return;
      }
      const fallback = 'Authentication failed. Please try again.';
      if (error instanceof Error && error.message) {
        setReauthError(error.message);
      } else {
        setReauthError(fallback);
      }
    }
  };

  const handleMFAConfirm = async () => {
    setReauthError('');
    try {
      const stepUp = await confirmMFAAndGetToken(mfaCode, 'recovery-codes');
      const response = await generateCodes({
        count: 8,
        stepUpToken: stepUp.token,
      });
      if (response.codes) {
        setRecoveryCodes(response.codes);
        setHasExistingCodes(true);
        setShowReauth(false);
        setMfaRequired(false);
        setMfaCode('');
        setPassword('');
      }
    } catch (error) {
      const fallback = 'MFA verification failed. Please try again.';
      if (error instanceof Error) {
        setReauthError(error.message || fallback);
      } else {
        setReauthError(fallback);
      }
    }
  };

  const handleSSOReauth = () => {
    sessionStorage.setItem('pending_recovery_code_generation', 'true');
    signInWithRedirect({ provider: { custom: ssoDomain! } });
  };

  const handleGenerateClick = () => {
    setShowReauth(true);
  };

  const handleCopyToClipboard = () => {
    const codesText = recoveryCodes.join('\n');
    navigator.clipboard
      .writeText(codesText)
      .then(() => {
        setCopied(true);
        toast.success('Recovery codes copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        toast.error('Failed to copy to clipboard');
      });
  };

  const handleClose = () => {
    setRecoveryCodes([]);
    setCopied(false);
    setShowReauth(false);
    setPassword('');
    setReauthError('');
    setMfaRequired(false);
    setMfaCode('');
    onClose();
  };

  const isLoading = isLoadingInfo || generateStatus === 'pending';

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Recovery Codes"
      size="xl"
      hideFooter
    >
      <div className="mb-4 flex flex-col gap-6">
        {/* Header Description */}
        <div className="flex flex-col gap-2">
          <p className="text-chariot-text-secondary text-sm">
            Recovery codes are one-time use codes that allow you to access your
            account if you lose access to your authenticator app. Keep them safe
            and secure.
          </p>
          <div className="bg-chariot-background-secondary flex items-center gap-2 rounded-md p-3">
            <div className="text-chariot-brand-primary">⚠️</div>
            <p className="text-chariot-text-secondary text-xs">
              <strong>Important:</strong> Store these codes securely. Each code
              can only be used once.
            </p>
          </div>
        </div>

        <Loader isLoading={isLoading} className="h-48">
          {/* Re-authentication Required */}
          {showReauth ? (
            <div className="flex flex-col gap-4 py-8">
              <h3 className="text-chariot-text-primary text-center text-lg font-semibold">
                Re-authentication Required
              </h3>
              <p className="text-chariot-text-secondary text-center text-sm">
                For security, please verify your identity before generating new
                recovery codes.
              </p>

              {isSSO ? (
                /* SSO Re-authentication */
                <div className="bg-chariot-background-secondary mx-auto max-w-md rounded-md p-6 text-center">
                  <p className="text-chariot-text-secondary mb-4 text-sm">
                    You will be redirected to your identity provider to
                    re-authenticate. After signing in, you&apos;ll return here
                    to generate your recovery codes.
                  </p>
                  <Button
                    onClick={handleSSOReauth}
                    label="Re-authenticate via SSO"
                    styleType="primary"
                    className="mx-auto"
                    disabled={isLoading}
                  />
                </div>
              ) : mfaRequired ? (
                /* MFA Verification */
                <div className="mx-auto w-full max-w-md space-y-4">
                  <Input
                    name="mfaCode"
                    label="MFA Code"
                    placeholder="Enter 6-digit code"
                    value={mfaCode}
                    onChange={e => setMfaCode(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && mfaCode.length === 6) {
                        handleMFAConfirm();
                      }
                    }}
                    error={reauthError}
                  />
                  <div className="flex justify-center gap-3">
                    <Button
                      onClick={() => {
                        setMfaRequired(false);
                        setMfaCode('');
                        setReauthError('');
                      }}
                      label="Back"
                      styleType="secondary"
                      disabled={isLoading}
                    />
                    <Button
                      onClick={handleMFAConfirm}
                      label="Verify & Generate"
                      styleType="primary"
                      disabled={isLoading || mfaCode.length !== 6}
                    />
                  </div>
                </div>
              ) : (
                /* Password Re-authentication */
                <div className="mx-auto w-full max-w-md space-y-4">
                  <Input
                    name="password"
                    type={Type.PASSWORD}
                    label="Password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        handleReauthGenerate();
                      }
                    }}
                    error={reauthError}
                  />
                  <div className="flex justify-center gap-3">
                    <Button
                      onClick={() => {
                        setShowReauth(false);
                        setPassword('');
                        setReauthError('');
                      }}
                      label="Cancel"
                      styleType="secondary"
                      disabled={isLoading}
                    />
                    <Button
                      onClick={handleReauthGenerate}
                      label="Confirm & Generate"
                      styleType="primary"
                      disabled={isLoading || !password}
                    />
                  </div>
                </div>
              )}
            </div>
          ) : /* Recovery Codes Display */
          recoveryCodes.length > 0 ? (
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <h3 className="text-chariot-text-primary text-lg font-semibold">
                  Your Recovery Codes
                </h3>
                <Button
                  onClick={handleCopyToClipboard}
                  startIcon={
                    copied ? (
                      <CheckIcon className="size-4 text-green-500" />
                    ) : (
                      <ClipboardDocumentIcon className="size-4" />
                    )
                  }
                  label={copied ? 'Copied!' : 'Copy All'}
                  styleType="secondary"
                  size="sm"
                />
              </div>

              {/* Codes Grid */}
              <div className="grid grid-cols-2 gap-3">
                {recoveryCodes.map((code, index) => (
                  <div
                    key={index}
                    className="border-chariot-stroke bg-chariot-background-primary flex items-center justify-center rounded-md border p-3"
                  >
                    <span className="text-chariot-text-primary font-mono text-sm">
                      {code}
                    </span>
                  </div>
                ))}
              </div>

              {/* Instructions */}
              <div className="border-chariot-stroke bg-chariot-background-secondary rounded-md border p-4">
                <h4 className="text-chariot-text-primary mb-2 text-sm font-semibold">
                  How to use recovery codes:
                </h4>
                <ul className="text-chariot-text-secondary space-y-1 text-xs">
                  <li>
                    • Save these codes in a secure location (password manager,
                    safe, etc.)
                  </li>
                  <li>
                    • Use a recovery code when you can&apos;t access your
                    authenticator app
                  </li>
                  <li>• Each code can only be used once</li>
                  <li>• Generate new codes if you&apos;ve used most of them</li>
                </ul>
              </div>
            </div>
          ) : (
            /* Generate New Codes */
            <div className="flex flex-col items-center gap-4 py-8">
              {hasExistingCodes ? (
                <div className="mx-auto text-center">
                  <h3 className="text-chariot-text-primary mb-2 text-lg font-semibold">
                    Recovery Codes Available
                  </h3>
                  <p className="text-chariot-text-secondary mb-4 text-sm">
                    You have existing recovery codes. Generate new codes to view
                    and replace them.
                  </p>
                  <Button
                    onClick={handleGenerateClick}
                    label="Generate New Codes"
                    styleType="primary"
                    className="mx-auto"
                    disabled={isLoading}
                  />
                </div>
              ) : (
                <div className="text-center">
                  <h3 className="text-chariot-text-primary mb-2 text-lg font-semibold">
                    No Recovery Codes
                  </h3>
                  <p className="text-chariot-text-secondary mb-4 text-sm">
                    Generate recovery codes to use as backup authentication when
                    you can&apos;t access your authenticator app.
                  </p>
                  <Button
                    className="mx-auto"
                    onClick={handleGenerateClick}
                    label="Generate Recovery Codes"
                    styleType="primary"
                    disabled={isLoading}
                  />
                </div>
              )}
            </div>
          )}
        </Loader>
      </div>
    </Modal>
  );
};
